import { Button, message, Space, Tag, Upload } from "antd";
import _, { update } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { apiRequest } from "../../api";
import apiPaths from "../../api/paths";
import { getMoreData } from "../../common";
import DynamicTable from "../../components/DynamicTable";
import EndRideModal from "./components/EndRideModal";
import SelectStationModal from "./components/SelectStationModal";
import * as XLSX from "xlsx";
import download from "downloadjs";
import { useHasEditPermission, useHasUploadPermission } from "../../common/hooks";
import RideModal from "./components/RideModal";
import { UploadOutlined } from "@ant-design/icons";
import DateRangeSelect from "../../components/DateRangeSelect";

const DEFAULT_COLUMNS = {
  ride_id: {
    show: true,
    label: "Ride Id",
    key: "ride_id",
  },
  vehicle_number: {
    show: true,
    label: "Vehicle Number",
    key: "vehicle_number",
  },
  rider: {
    show: true,
    label: "Rider",
    key: "rider",
  },
  duration: {
    show: true,
    label: "Duration",
    key: "duration",
  },
  cost: {
    show: true,
    label: "Cost",
    key: "cost",
  },
  transaction: {
    show: true,
    label: "Type",
    key: "transaction",
  },
  start_location: {
    show: false,
    label: "Start Location",
    key: "start_location",
  },
  start_date: {
    show: true,
    label: "Start Date",
    key: "start_date",
  },
  end_date: {
    show: true,
    label: "End Date",
    key: "end_date",
  },
  end_location: {
    show: false,
    label: "End Location",
    key: "end_location",
  },
  start_dock_station: {
    show: true,
    label: "Start Station",
    key: "start_dock_station",
  },
  end_dock_station: {
    show: true,
    label: "End Station",
    key: "end_dock_station",
  },
  end_location: {
    show: false,
    label: "End Location",
    key: "end_location",
  },
  ride_started_by: {
    show: false,
    label: "Started By",
    key: "ride_started_by",
  },
  ride_ended_by: {
    show: false,
    label: "Ended By",
    key: "ride_ended_by",
  },
  ride_cancelled_by: {
    show: false,
    label: "Cancelled By",
    key: "ride_cancelled_by",
  },
  ride_cancelled: {
    show: true,
    label: "Cancel Reason",
    key: "ride_cancelled",
  },
};

function Rides() {
  const [rides, setRides] = useState([]);
  const [page, setPage] = useState(1);
  const [stations, setStations] = useState([]);
  const [showEndRide, setShowEndRide] = useState(null);
  const [showSelectStation, setShowSelectStation] = useState(null);
  const [filteredRides, setFilteredRides] = useState([]);
  const [showRideModal, setShowRideModal] = useState(false);
  const [selectedRange, setSelectedRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);
  const allowEdit = useHasEditPermission();
  const allowUpload = useHasUploadPermission();

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    fetchRides(selectedRange);
  }, [selectedRange]);

  const fetchStations = async () => {
    const res = await apiRequest({
      path: apiPaths.stations,
    });
    if (!res.error) {
      setStations(res.results);
    }
  };

  const fetchRides = async (dates) => {
    let startDate = dates[0];
    let endDate = dates[1];
    const res = await apiRequest({
      path: apiPaths.rides+`?start_date=${startDate}&end_date=${endDate}`,
    });
    if (!res.error) {
      if (res.next) {
        return new Promise((resolve, reject) => {
          getMoreData({
            path: res.next,
            data: res.results,
            onFinish: (data) => {
              setRides(data);
              setFilteredRides(data);
              resolve({ results: data });
            },
          });
        });
      } else {
        setRides(res.results);
        setFilteredRides(res.results);
      }
    } else {
      message.error(res.error_description);
    }
  };

  const handleRideAdd = async (data) => {
    setRides([data, ...rides]);
    setShowRideModal(false);
  };

  const handleRideUpdate = (res) => {
    let rideIndex = rides.findIndex((item) => res._id == item._id);
    let newRides = [...rides];
    newRides[rideIndex] = {
      ...newRides[rideIndex],
      ...res,
    };
    setRides(newRides);
  };

  const exportXlsx = () => {
    let exportUsers = filteredRides.reduce((acc, curr) => {
      let {
        rider,
        end_dock_station,
        start_dock_station,
        end_location,
        start_location,
        id,
        ride_ended_by,
        ride_started_by,
        ...rest
      } = curr;
      let rideStatus = curr.ride_cancelled
        ? "Cancelled"
        : curr.end_date
        ? "Completed"
        : "In progress";
      let userDetails = {
        ride_id: curr.ride_id,
        vehicle_number: curr.vehicle_number,
        rider: rider?.username,
        start_date: curr.start_date,
        end_date: curr.end_date,
        ride_status: rideStatus,
        duration: curr.duration,
        cost: curr.cost,
        cost_change_reason: curr.cost_change_reason,
        start_location: start_location?.coordinates?.join(","),
        end_location: end_location?.coordinates?.join(","),
        start_dock_station: start_dock_station?.name,
        end_dock_station: end_dock_station?.name,
        ride_started_by: ride_started_by?.username,
        ride_ended_by: ride_ended_by?.username,
        ride_cancelled_by: curr.ride_cancelled_by?.username,
        ride_cancelled_reason: curr.ride_cancelled_reason,
      };
      acc.push(userDetails);
      return acc;
    }, []);
    let workbook = XLSX.utils.book_new();
    let worksheet = XLSX.utils.json_to_sheet(exportUsers);
    var range = XLSX.utils.decode_range(worksheet["!ref"]);
    worksheet["!cols"] = [...Array(range.e.c)].map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(workbook, worksheet, "sheet_1");
    let data = XLSX.write(workbook, { type: "array" });
    download(
      data,
      "rides.xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;"
    );
  };

  const getColor = (type) => {
    switch (type) {
      case "upi-ride":
        return "purple";
      case "cash-ride":
        return "yellow";
      default:
        return "green";
    }
  };

  const COLUMNS = [
    {
      title: "Ride Id",
      dataIndex: "ride_id",
      fixed: "left",
      width: 120,
      filterType: "search",
    },
    {
      title: "Vehicle Number",
      dataIndex: "vehicle_number",
      width: 140,
      filterType: "search",
      fixed: "left",
    },
    {
      title: "Rider",
      dataIndex: "rider.username",
      width: "150px",
      filterType: "search",
      render: (_, record) => {
        return (
          <div>
            <div>{record.rider?.fullname}</div>
            <div style={{ color: "#a7a7a7", fontSize: 11 }}>
              {record.rider?.username}
            </div>
          </div>
        );
      },
    },
    {
      title: "Duration",
      dataIndex: "duration",
      width: "120px",
      render: (duration) => {
        if (duration == undefined) {
          return null;
        }
        let str = "";
        if (duration > 60) {
          str += Math.floor(duration / 60) + " hr ";
        }
        str += (duration % 60) + " min";

        return <div>{str}</div>;
      },
    },
    {
      title: "Cost",
      dataIndex: "cost",
      width: "90px",
      render: (cost) => {
        return <div>{cost}</div>;
      },
    },
    {
      title: "Type",
      dataIndex: "transaction.type",
      width: "100px",
      filters: [
        {
          text: "Wallet Ride",
          value: "ride",
        },
        {
          text: "UPI Ride",
          value: "upi-ride",
        },
        {
          text: "Cash Ride",
          value: "cash-ride",
        },
        {
          text: "Day Pass Ride",
          value: "day-pass-ride",
        },
      ],
      onFilter: (value, record) => value == record.type,
      render: (value, record) => {
        return <Tag color={getColor(record.type)}>{record.type}</Tag>;
      },
    },
    {
      title: "Start Station",
      dataIndex: "start_dock_station.name",
      width: "130px",
      filterType: "search",
      render: (_, record) => {
        return (
          <div>
            {record.start_dock_station ? (
              <div>{record.start_dock_station?.name}</div>
            ) : allowEdit ? (
              <Button
                size="small"
                onClick={() => {
                  setShowSelectStation({
                    isStart: true,
                    rideId: record._id,
                  });
                }}
                type="primary"
              >
                Add Station
              </Button>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "End Station",
      dataIndex: "end_dock_station.name",
      filterType: "search",
      width: "130px",
      render: (_, record) => {
        return (
          <div>
            {record.end_dock_station ? (
              <div>{record.end_dock_station.name}</div>
            ) : allowEdit ? (
              <Button
                onClick={() => {
                  setShowSelectStation({
                    isStart: false,
                    rideId: record._id,
                  });
                }}
                size="small"
                type="primary"
              >
                Add Station
              </Button>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Start Location",
      dataIndex: "start_location",
      width: "220px",
      render: (_, record) => {
        return <div>{record.start_location?.coordinates?.join(",")}</div>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      width: "150px",
      filterType: "date",
      render: (value) => {
        return <div>{moment(value).format("DD-MM-YYYY HH:mm")}</div>;
      },
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      width: "150px",
      filterType: "date",
      render: (value, record) => {
        return <div>{moment(value).format("DD-MM-YYYY HH:mm")}</div>;
      },
    },
    {
      title: "End Location",
      dataIndex: "end_location",
      width: "220px",
      render: (_, record) => {
        return <div>{record.end_location?.coordinates?.join(",")}</div>;
      },
    },

    {
      title: "Started By",
      dataIndex: "ride_started_by.username",
      width: "170px",
      filterType: "search",
      render: (_, { ride_started_by: user }) => {
        if (!user) return null;
        return (
          <div>
            <div>{user.fullname}</div>
            <div style={{ color: "#a7a7a7", fontSize: 11 }}>
              {user?.username}
            </div>
          </div>
        );
      },
    },
    {
      title: "Ended By",
      dataIndex: "ride_ended_by.username",
      filterType: "search",
      width: "170px",
      render: (_, { ride_ended_by: user }) => {
        if (!user) return null;
        return (
          <div>
            <div>{user.fullname}</div>
            <div style={{ color: "#a7a7a7", fontSize: 11 }}>
              {user?.username}
            </div>
          </div>
        );
      },
    },
    {
      title: "Cancelled By",
      dataIndex: "ride_cancelled_by.username",
      width: "170px",
      filterType: "search",
      render: (_, { ride_cancelled_by: user }) => {
        if (!user) return null;
        return (
          <div>
            <div>{user.fullname}</div>
            <div style={{ color: "#a7a7a7", fontSize: 11 }}>
              {user?.username}
            </div>
          </div>
        );
      },
    },
    {
      title: "Ride Status",
      dataIndex: "ride_cancelled",
      width: "190px",
      fixed: "right",
      filters: [
        { text: "In Progress", value: "in-progress" },
        { text: "Completed", value: "completed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      onFilter: (value, record) => {
        if (value == "completed" && record.end_date && !record.ride_cancelled) {
          return true;
        } else if (value == "cancelled" && record.ride_cancelled) {
          return true;
        } else if (
          value == "in-progress" &&
          !record.end_date &&
          !record.ride_cancelled
        ) {
          return true;
        }
        return false;
      },
      render: (value, record) => {
        return value ? (
          <>
            <Tag color={"red"}>Cancelled</Tag>
            <div style={{ fontSize: 12 }}>
              <span style={{ color: "#b7b7b7" }}>Reason:</span> {value}
            </div>
          </>
        ) : record.end_date ? (
          <Tag color={"green"}>Completed</Tag>
        ) : (
          <>
            <Tag color={"yellow"}>In Progress</Tag>
            {allowEdit ? (
              <div style={{ marginTop: 10 }}>
                <Space>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowEndRide({
                        vehicle: record.vehicle_number,
                        isCancel: false,
                      });
                    }}
                  >
                    End Ride
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowEndRide({
                        vehicle: record.vehicle_number,
                        isCancel: true,
                      });
                    }}
                  >
                    Cancel Ride
                  </Button>
                </Space>
              </div>
            ) : null}
          </>
        );
      },
    },
  ];

  const handleDelete = async (row) => {
    const res = await apiRequest({
      path: apiPaths.rides + `/${row._id}`,
      method: "DELETE",
    });
    if (!res.error) {
      let updatedRides = rides.filter((ride) => ride._id != row._id);
      setRides(updatedRides);
      message.success("Ride Deleted");
      return;
    }
    message.error(res.error_description);
  };

  const uploadRides = async (data) => {
    let errorMessage = "";
    const payload = data.reduce(
      (acc, ride, index) => {
        if (ride.ride_id) {
          acc.update_rides.push(ride);
        } else {
          if (
            !ride.vehicle_number ||
            !ride.start_date ||
            !ride.cost ||
            !ride.rider
          ) {
            errorMessage += `,row-${index + 1}`;
          }
          acc.new_rides.push(ride);
        }
        return acc;
      },
      { update_rides: [], new_rides: [] }
    );
    if (errorMessage) {
      message.error(
        `Following rows has missing fields vehicle_number/start_date/cost/rider ${errorMessage}`,
        10
      );
      return;
    }
    const res = await apiRequest({
      path: apiPaths.rides + `/bulk`,
      method: "POST",
      data: payload,
    });
    if (res.error) {
      message.error(res.error_description);
      return;
    }
    fetchRides(selectedRange);

    message.success(`Rides uploaded successfully!`);
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const json = XLSX.utils.sheet_to_json(worksheet);

        uploadRides(json);
      };

      reader.readAsArrayBuffer(file);

      // Stop automatic upload
      return false;
    },
    accept: ".xlsx,.xls",
    showUploadList: false,
  };

  return (
    <div style={{ height: "80vh" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Space>
          <DateRangeSelect
            selectedRange={selectedRange}
            onSelectedRangeChanged={setSelectedRange}
          />
          {allowUpload ? (
            <>
              <Button
                onClick={() => {
                  setShowRideModal(true);
                }}
              >
                Add Ride
              </Button>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Upload file</Button>
              </Upload>
            </>
          ) : null}
          <Button type="primary" onClick={exportXlsx}>
            Export
          </Button>
        </Space>
      </div>
      <div style={{ height: "90%", marginTop: 30 }}>
        <DynamicTable
          dataSource={rides}
          columns={COLUMNS}
          defaultColumns={DEFAULT_COLUMNS}
          pagination={{
            onChange(current) {
              setPage(current);
            },
          }}
          deleteBtn
          requireEdit={false}
          onRowDelete={handleDelete}
          scroll={{ y: "65vh" }}
          showAction={() => allowEdit}
          onChange={(_, _a, _b, { currentDataSource }) => {
            setFilteredRides(currentDataSource);
          }}
        />
      </div>
      {showRideModal ? (
        <RideModal
          open={true}
          onCancel={() => {
            setShowRideModal(false);
          }}
          onSave={handleRideAdd}
          stations={stations}
        />
      ) : null}
      {showEndRide ? (
        <EndRideModal
          vehicle={showEndRide.vehicle}
          isCancel={showEndRide.isCancel}
          onUpdated={(res) => {
            handleRideUpdate(res);
            setShowEndRide(null);
          }}
          stations={stations}
          onCancel={() => {
            setShowEndRide(null);
          }}
          open={true}
        />
      ) : null}
      {showSelectStation ? (
        <SelectStationModal
          rideId={showSelectStation.rideId}
          isStart={showSelectStation.isStart}
          onUpdated={(res) => {
            handleRideUpdate(res);
            setShowSelectStation(null);
          }}
          stations={stations}
          onCancel={() => {
            setShowSelectStation(null);
          }}
          open={true}
        />
      ) : null}
    </div>
  );
}

export default Rides;
